/**
 * Supervisor Agent — dedicated page with Overview / Logs / Settings tabs.
 * Surfaces the Pro autopilot feature out of Settings into its own screen.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Badge, EmptyState, Spinner } from '../components/ui';
import { SupervisorIcon } from '../components/NavIcons';
import { AgentTabs, type AgentTab } from '../components/agents/agentsCommon';
import SupervisorSettings from '../components/agents/SupervisorSettings';
import { getSupervisorStatus, runSupervisorNow, getJobStatus } from '../api';
import type { PageKey, SupervisorStatus, SupervisorRun, Workspace } from '../types';
import '../components/agents/agents.css';

interface Props {
  workspace: Workspace;
  onWorkspaceUpdate: (_ws: Workspace) => void;
  onNavigate: (_page: PageKey) => void;
}

function relTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function runVariant(status: SupervisorRun['status']): 'approved' | 'error' | 'scheduled' {
  if (status === 'completed') return 'approved';
  if (status === 'failed') return 'error';
  return 'scheduled';
}

export default function SupervisorPage({ workspace, onWorkspaceUpdate, onNavigate }: Props) {
  const [tab, setTab] = useState<AgentTab>('overview');
  const [data, setData] = useState<SupervisorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [pollJobId, setPollJobId] = useState<string | null>(null);
  const canUse = workspace.has_autopilot ?? false;

  const fetchStatus = useCallback(async () => {
    try {
      setData(await getSupervisorStatus(workspace.id));
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [workspace.id]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!pollJobId) return;
    const t = setInterval(async () => {
      try {
        const job = await getJobStatus(pollJobId);
        if (job.status === 'completed' || job.status === 'failed') {
          setPollJobId(null);
          setTriggering(false);
          await fetchStatus();
        }
      } catch {
        setPollJobId(null);
        setTriggering(false);
      }
    }, 2000);
    return () => clearInterval(t);
  }, [pollJobId, fetchStatus]);

  const runNow = async () => {
    setTriggering(true);
    try {
      const res = await runSupervisorNow(workspace.id);
      setPollJobId(res.job_id);
      await fetchStatus();
    } catch {
      setTriggering(false);
    }
  };

  const runs = data?.recent_runs ?? [];
  const isRunning =
    !!pollJobId || runs[0]?.status === 'queued' || runs[0]?.status === 'running';

  return (
    <div className="agent-page">
      <div className="agent-hero">
        <div className="agent-hero__icon">
          <SupervisorIcon size={26} />
        </div>
        <div>
          <div className="agent-hero__title">
            Supervisor Agent
            {!canUse && <span className="settings-plan-lock-badge">🔒 Pro</span>}
          </div>
          <div className="agent-hero__subtitle">
            Your autopilot. Every cycle it audits the content pipeline, fills gaps, and keeps
            posts flowing — so your calendar never runs dry.
          </div>
        </div>
        {canUse && (
          <div className="agent-hero__actions">
            <Button variant="primary" size="sm" loading={triggering} disabled={isRunning} onClick={runNow}>
              {isRunning ? 'Running…' : 'Run now'}
            </Button>
          </div>
        )}
      </div>

      <AgentTabs active={tab} onChange={setTab} />

      {loading ? (
        <Spinner size="md" />
      ) : tab === 'settings' ? (
        <SupervisorSettings workspace={workspace} onWorkspaceUpdate={onWorkspaceUpdate} />
      ) : !canUse ? (
        <EmptyState
          icon={<SupervisorIcon size={28} />}
          title="The Supervisor Agent is a Pro feature"
          description="Upgrade to Pro to let Conistra run your content pipeline on autopilot."
          action={
            <Button variant="primary" size="sm" onClick={() => onNavigate('settings')}>
              Upgrade to Pro
            </Button>
          }
        />
      ) : tab === 'overview' ? (
        <>
          <div className="agent-status-line">
            <span>
              Status:{' '}
              <strong>{data?.supervisor_enabled ? 'Active' : 'Inactive'}</strong>
            </span>
            <span>
              Last run: <strong>{relTime(data?.supervisor_last_run_at)}</strong>
            </span>
            {data?.supervisor_enabled && (
              <span>
                Next run: <strong>{relTime(data?.next_run_at)}</strong>
              </span>
            )}
          </div>
          <div className="agent-stats">
            <div className="agent-stat">
              <div className="agent-stat__value">{data?.supervisor_interval_hours ?? '—'}h</div>
              <div className="agent-stat__label">Run interval</div>
            </div>
            <div className="agent-stat">
              <div className="agent-stat__value">{data?.supervisor_min_outlines ?? '—'}</div>
              <div className="agent-stat__label">Min outlines kept</div>
            </div>
            <div className="agent-stat">
              <div className="agent-stat__value">{runs.length}</div>
              <div className="agent-stat__label">Recent runs</div>
            </div>
          </div>
          {runs[0]?.output?.summary && (
            <p className="agent-log-row__incoming">Last result: {runs[0].output.summary}</p>
          )}
        </>
      ) : (
        // Logs tab
        <>
          {runs.length === 0 ? (
            <EmptyState title="No runs yet" description="The supervisor hasn't run for this workspace yet." />
          ) : (
            <div className="agent-log-list">
              {runs.map((r) => (
                <div key={r.job_id} className="agent-log-row">
                  <div className="agent-log-row__main">
                    <div className="agent-log-row__meta">
                      <Badge variant={runVariant(r.status)} dot>
                        {r.status}
                      </Badge>
                      {r.output?.status && <span>{r.output.status}</span>}
                    </div>
                    {r.output?.summary && (
                      <div className="agent-log-row__incoming">{r.output.summary}</div>
                    )}
                    {r.error && <div className="agent-log-row__reply">⚠ {r.error}</div>}
                  </div>
                  <div className="agent-log-row__time">{relTime(r.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
